import { useState } from 'react'
import { authFetch, apiUrl } from '../../lib/api'

type CompareState = 'idle' | 'loading' | 'comparing' | 'revealed'

interface CompareResult {
  id: string
  responseA: string
  responseB: string
  _models: string
}

interface VoteResult {
  modelA: string
  modelB: string
  winner: string
}

interface LeaderboardEntry {
  model: string
  wins: number
}

export default function ModelComparePanel() {
  const [prompt, setPrompt] = useState('')
  const [state, setState] = useState<CompareState>('idle')
  const [result, setResult] = useState<CompareResult | null>(null)
  const [vote, setVote] = useState<VoteResult | null>(null)
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [totalVotes, setTotalVotes] = useState(0)
  const [error, setError] = useState('')

  const runCompare = async () => {
    if (!prompt.trim()) return
    setState('loading')
    setError('')
    setResult(null)
    setVote(null)

    try {
      const res = await authFetch(apiUrl('/api/compare'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim() }),
      })
      if (!res.ok) { setError('Failed to compare'); setState('idle'); return }
      const data = await res.json()
      setResult(data)
      setState('comparing')
    } catch (e: any) {
      setError(e.message ?? 'Network error')
      setState('idle')
    }
  }

  const submitVote = async (winner: 'a' | 'b' | 'tie') => {
    if (!result) return
    try {
      const res = await authFetch(apiUrl('/api/compare/vote'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim(), winner, _models: result._models }),
      })
      if (res.ok) {
        const data = await res.json()
        setVote(data)
        setState('revealed')
        loadLeaderboard()
      }
    } catch {}
  }

  const loadLeaderboard = async () => {
    try {
      const res = await authFetch(apiUrl('/api/compare/stats'))
      if (res.ok) {
        const data = await res.json()
        setLeaderboard(data.leaderboard ?? [])
        setTotalVotes(data.totalVotes ?? 0)
      }
    } catch {}
  }

  const reset = () => {
    setState('idle')
    setResult(null)
    setVote(null)
    setPrompt('')
  }

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-0)' }}>⚔️ Blind Model Compare</h2>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-2)' }}>
          Same prompt → 2 anonymous models → you pick the winner
        </p>
      </div>

      {/* Prompt input */}
      {state === 'idle' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Type a coding question or prompt..."
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runCompare() } }}
            style={{
              flex: 1, minHeight: 60, padding: 10, borderRadius: 8,
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              color: 'var(--text-0)', fontSize: 13, resize: 'vertical',
              fontFamily: 'var(--font-mono)',
            }}
          />
          <button
            onClick={runCompare}
            disabled={!prompt.trim()}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: prompt.trim() ? 'var(--accent)' : 'var(--bg-3)',
              color: prompt.trim() ? '#fff' : 'var(--text-2)',
              cursor: prompt.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 600, fontSize: 13, alignSelf: 'flex-end',
            }}
          >
            Compare
          </button>
        </div>
      )}

      {/* Loading */}
      {state === 'loading' && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-2)' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          Sending to 2 models in parallel...
        </div>
      )}

      {/* Side-by-side responses */}
      {(state === 'comparing' || state === 'revealed') && result && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
            Prompt: <span style={{ color: 'var(--text-1)' }}>{prompt}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <ResponseCard
              label="Model A"
              text={result.responseA}
              revealed={state === 'revealed' ? vote?.modelA : undefined}
              isWinner={vote?.winner === vote?.modelA}
              onVote={() => submitVote('a')}
              canVote={state === 'comparing'}
            />
            <ResponseCard
              label="Model B"
              text={result.responseB}
              revealed={state === 'revealed' ? vote?.modelB : undefined}
              isWinner={vote?.winner === vote?.modelB}
              onVote={() => submitVote('b')}
              canVote={state === 'comparing'}
            />
          </div>

          {state === 'comparing' && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button onClick={() => submitVote('tie')} style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 12,
              }}>
                🤝 It's a tie
              </button>
            </div>
          )}

          {state === 'revealed' && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button onClick={reset} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: 'var(--accent)', color: '#fff', cursor: 'pointer',
                fontWeight: 600, fontSize: 13,
              }}>
                Try another prompt
              </button>
            </div>
          )}
        </>
      )}

      {error && <div style={{ color: 'var(--red)', marginTop: 8, fontSize: 12 }}>{error}</div>}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div style={{ marginTop: 24, padding: 12, background: 'var(--bg-1)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)', marginBottom: 8 }}>
            🏆 Leaderboard ({totalVotes} votes)
          </div>
          {leaderboard.map((entry, i) => (
            <div key={entry.model} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: 'var(--text-1)' }}>
              <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '  '} {entry.model}</span>
              <span style={{ color: 'var(--accent)' }}>{entry.wins} wins</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ResponseCard({ label, text, revealed, isWinner, onVote, canVote }: {
  label: string
  text: string
  revealed?: string | undefined
  isWinner?: boolean | undefined
  onVote: () => void
  canVote: boolean
}) {
  return (
    <div style={{
      background: 'var(--bg-1)', border: `1px solid ${isWinner ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
      boxShadow: isWinner ? '0 0 8px rgba(124,110,245,0.3)' : 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)' }}>
          {revealed ? `${label} → ${revealed}` : label}
          {isWinner && ' 🏆'}
        </span>
        {canVote && (
          <button onClick={onVote} style={{
            padding: '4px 10px', borderRadius: 6, border: 'none',
            background: 'var(--accent)', color: '#fff', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
          }}>
            This wins
          </button>
        )}
      </div>
      <pre style={{
        margin: 0, fontSize: 12, color: 'var(--text-1)', whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', maxHeight: 300, overflow: 'auto',
        fontFamily: 'var(--font-mono)', lineHeight: 1.5,
      }}>
        {text || '(empty response)'}
      </pre>
    </div>
  )
}
